import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { type Product } from '@/constants/products'
import { Check, Copy, Terminal } from 'lucide-react'
import { cn } from '@/lib/cn'

interface ProductEngineRoomProps {
  product: Product
}

export function ProductEngineRoom({ product }: ProductEngineRoomProps) {
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [copied, setCopied] = useState(false)

  const handleCopyPayload = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(product.schemaJson, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback
    }
  }

  // Syntax coloring helper for lines
  const jsonLines = JSON.stringify(product.schemaJson, null, 2).split('\n')

  return (
    <section id="engine-room" className="py-12 sm:py-16 border-b border-border-subtle scroll-mt-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        {/* Left Column: Technical Stepper Pipeline */}
        <div className="lg:col-span-5 space-y-6">
          <div className="space-y-2">
            <span className="font-mono text-xs uppercase tracking-widest text-fg-muted block">
              // Inside The Core
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-fg text-balance">
              How data moves through the engine.
            </h2>
            <p className="text-sm text-fg-subtle leading-relaxed">
              Explore the sequential stages of validation, processing, and multi-node synchronization that drive the {product.name} architecture.
            </p>
          </div>

          {/* Stepper Timeline */}
          <div className="relative pl-6 ml-3 space-y-6 border-l border-border pt-2 pb-2">
            {product.engineSteps.map((step, idx) => {
              const isActive = activeStepIndex === idx

              return (
                <button
                  type="button"
                  key={step.stepNumber}
                  onClick={() => setActiveStepIndex(idx)}
                  className={cn(
                    'w-full text-left relative group rounded-sm p-3 -ml-3 transition-all duration-150 cursor-pointer select-none',
                    isActive
                      ? 'bg-surface-2 border border-border shadow-xs'
                      : 'hover:bg-surface-2/60 border border-transparent'
                  )}
                >
                  {/* Step Marker on the line */}
                  <span
                    className={cn(
                      'absolute -left-[22px] top-4 w-4 h-4 rounded-full flex items-center justify-center font-mono text-[9px] transition-all duration-200',
                      isActive
                        ? 'bg-fg text-surface-0 ring-4 ring-surface-0 font-bold'
                        : 'bg-surface-3 border border-border-strong text-fg-muted group-hover:border-fg'
                    )}
                    aria-hidden="true"
                  />

                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={cn(
                        'font-mono text-xs font-semibold',
                        isActive ? 'text-fg' : 'text-fg-muted group-hover:text-fg-subtle'
                      )}
                    >
                      {step.stepNumber} //
                    </span>
                    <h3
                      className={cn(
                        'font-semibold text-sm sm:text-base tracking-tight transition-colors',
                        isActive ? 'text-fg' : 'text-fg-subtle group-hover:text-fg'
                      )}
                    >
                      {step.title}
                    </h3>
                  </div>

                  <p
                    className={cn(
                      'text-xs sm:text-sm leading-relaxed transition-colors',
                      isActive ? 'text-fg-subtle' : 'text-fg-muted'
                    )}
                  >
                    {step.description}
                  </p>
                </button>
              )
            })}
          </div>
        </div>

        {/* Right Column: Terminal Window Sandbox */}
        <div className="lg:col-span-7 w-full">
          <Card surface="2" className="border-border p-0 overflow-hidden shadow-2xl">
            {/* Window Chrome Header */}
            <div className="px-4 py-3 border-b border-border bg-surface-3 flex items-center justify-between select-none">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5" aria-hidden="true">
                  <span className="w-2.5 h-2.5 rounded-full bg-border-strong inline-block" />
                  <span className="w-2.5 h-2.5 rounded-full bg-border-strong inline-block" />
                  <span className="w-2.5 h-2.5 rounded-full bg-border-strong inline-block" />
                </div>
                <div className="flex items-center gap-1.5 ml-2">
                  <Terminal className="w-3.5 h-3.5 text-fg-muted" />
                  <span className="font-mono text-xs text-fg font-medium tracking-wide">
                    {product.schemaFilename}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCopyPayload}
                className="flex items-center gap-1.5 font-mono text-[11px] px-2.5 py-1 rounded-xs bg-surface-1 hover:bg-surface-2 text-fg border border-border transition-colors cursor-pointer"
                title="Copy schema JSON to clipboard"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3 text-success" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3 text-fg-muted" />
                    <span>Copy Payload</span>
                  </>
                )}
              </button>
            </div>

            {/* Code Body */}
            <pre className="p-4 sm:p-6 font-mono text-xs overflow-x-auto bg-surface-1 text-fg leading-relaxed max-h-[460px] select-text">
              <code>
                {jsonLines.map((line, index) => {
                  // Render subtle syntax colors using semantic design tokens
                  const isKey = line.includes('":')
                  return (
                    <div key={index} className="table-row">
                      <span className="table-cell select-none text-fg-muted/40 pr-4 text-right w-8 text-[11px]">
                        {index + 1}
                      </span>
                      <span
                        className={cn(
                          'table-cell whitespace-pre',
                          isKey ? 'text-fg' : 'text-fg-subtle'
                        )}
                      >
                        {line}
                      </span>
                    </div>
                  )
                })}
              </code>
            </pre>
          </Card>
        </div>
      </div>
    </section>
  )
}
